// One row of a sidebar (the Me nav, favorites, settings). Every sidebar uses this
// row, so they look and behave the same: 32 px, icon and label, the current row
// colored by background and text color (never bold).
import { Link } from "@tanstack/react-router";
import type { ComponentType, ReactNode } from "react";

export const ROW =
  "group relative flex h-8 min-w-0 items-center gap-2.5 rounded-[7px] px-2.5 text-[13px] text-sidebar-fg hover:bg-sidebar-hover";
// `!` keeps the current row its own ground under the pointer (the hover is lighter).
export const ROW_CURRENT = "bg-sidebar-active! text-sidebar-fg-strong";

export function NavRow({
  to,
  icon: IconComponent,
  label,
  end,
  exact = false,
  current,
}: {
  to: string;
  icon?: ComponentType<{ className?: string }>;
  label: string;
  /** At the end of the row: a dot or a count (only when there is something to do). */
  end?: ReactNode;
  exact?: boolean;
  /**
   * Whether the row is the current place, when its link alone cannot tell
   * (a section that owns several paths). Without it, the link's own match.
   */
  current?: boolean;
}) {
  return (
    <Link
      to={to}
      activeOptions={{ exact, includeSearch: false }}
      className={current ? `${ROW} ${ROW_CURRENT}` : ROW}
      aria-current={current ? "page" : undefined}
      activeProps={
        current === undefined
          ? { className: ROW_CURRENT, "aria-current": "page" }
          : {}
      }
    >
      {IconComponent && <IconComponent className="text-sidebar-muted" />}
      {/* The folded column shows the icon alone: the name and the end fade
          in as the full sidebar grows out of it (styles: `data-fold-label`). */}
      <span data-fold-label="" className="min-w-0 flex-1 truncate">
        {label}
      </span>
      {end && (
        <span
          data-fold-label=""
          className="flex-none tabular-nums text-sidebar-muted"
        >
          {end}
        </span>
      )}
    </Link>
  );
}

/**
 * A heading between groups of rows: small, spaced, faint, never bold. Its
 * height is fixed (a folded column puts a rule of the same height in its
 * place, so the icons under it stay put).
 */
export function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <div
      data-fold-label=""
      className="mt-5 mb-1 px-2.5 text-[11px] leading-4 tracking-[0.04em] text-sidebar-muted"
    >
      {children}
    </div>
  );
}
